import React from "react";
import { Trash2, Plus } from "lucide-react";
import TravelFormInput from "src/components/travel/form/TravelFormInput";
import TravelFormSelect from "src/components/travel/form/TravelFormSelect";
import { EMPTY_FLIGHT, EMPTY_HOTEL, EMPTY_CAR, carSpecialRequestOptions } from "src/helpers/travelHelper";

const TravelPreferences = ({ formData, setFormData, preferencesData }) => {
  const mealOptions = (preferencesData?.meal_preferences || []).map((m) => ({ value: m.code, label: m.name }));
  const airlineOptions = (preferencesData?.air_providers || []).map((a) => ({ value: a.code, label: a.name }));
  const hotelOptions = (preferencesData?.hotel_chains || []).map((h) => ({ value: h.code, label: h.name }));
  const carOptions = (preferencesData?.car_chains || []).map((c) => ({ value: c.code, label: c.name }));

  const seatOptions = [
    { value: "A", label: "Aisle" },
    { value: "W", label: "Window" },
    // { value: "MIDDLE", label: "Middle" },
  ];

  const handlePreferenceChange = (field, value) => {
    console.log({ field, value });
    setFormData({ ...formData, preferences: { ...formData.preferences, [field]: value } });
  };

  const handleMembershipChange = (type, index, field, value) => {
    const updatedArray = [...formData.memberships[type]];
    updatedArray[index] = { ...updatedArray[index], [field]: value };
    setFormData({ ...formData, memberships: { ...formData.memberships, [type]: updatedArray } });
  };

  const addMembership = (type, template) => {
    setFormData({ ...formData, memberships: { ...formData.memberships, [type]: [...formData.memberships[type], template] } });
  };

  const removeMembership = (type, index) => {
    const updatedArray = formData.memberships[type].filter((_, i) => i !== index);
    setFormData({ ...formData, memberships: { ...formData.memberships, [type]: updatedArray } });
  };

  const flights = formData.memberships?.flight || [];
  const hotels = formData.memberships?.hotel || [];
  const cars = formData.memberships?.rentalCar || [];
  const prefs = formData.preferences || {};

  return (
    <div className="col-span-12 space-y-10">
      <div>
        <h4 className="mb-4 border-b border-gray-100 pb-2 text-lg font-bold text-[#2D0B31]">Preferences</h4>
        <div className="grid grid-cols-1 gap-6 rounded-xl border border-gray-200 bg-gray-50/50 p-5 md:grid-cols-2">
          <div className="col-span-1 md:col-span-2">
            <TravelFormSelect
              label="Seat Request"
              options={seatOptions}
              value={prefs.seatRequest}
              onChange={(e) => handlePreferenceChange("seatRequest", e.target.value)}
            />
            <p className="mt-1.5 text-xs font-medium text-gray-500">We do our best to accommodate requests but cannot guarantee them.</p>
          </div>

          <div className="col-span-1 md:col-span-2">
            <TravelFormSelect
              label="Meal Request"
              options={mealOptions}
              value={prefs.mealRequest}
              onChange={(e) => handlePreferenceChange("mealRequest", e.target.value)}
            />
          </div>

          <div className="col-span-1 flex items-center gap-2 md:col-span-2">
            <input
              type="checkbox"
              id="smoker-check"
              checked={prefs.smoker}
              onChange={(e) => handlePreferenceChange("smoker", e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-[#2D0B31] focus:ring-[#2D0B31]"
            />
            <label
              htmlFor="smoker-check"
              className="cursor-pointer text-sm font-bold text-gray-700">
              Smoker
            </label>
          </div>
        </div>
      </div>

      <div>
        <h4 className="mb-4 border-b border-gray-100 pb-2 text-lg font-bold text-[#2D0B31]">Frequent Flyer</h4>
        {flights.map((item, index) => (
          <div
            key={index}
            className="group relative mb-6 rounded-xl border border-gray-200 bg-gray-50/50 p-5 transition-all hover:border-[#2D0B31]/30">
            <div className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-2">
              <TravelFormSelect
                label="Airline"
                options={airlineOptions}
                value={item.alliance}
                onChange={(e) => handleMembershipChange("flight", index, "alliance", e.target.value)}
                required
              />
              <TravelFormInput
                label="Number"
                value={item.memberNumber}
                onChange={(e) => handleMembershipChange("flight", index, "memberNumber", e.target.value)}
                required
              />
              <TravelFormInput
                label="PIN"
                value={item.pin}
                onChange={(e) => handleMembershipChange("flight", index, "pin", e.target.value)}
              />
            </div>
            <button
              type="button"
              onClick={() => removeMembership("flight", index)}
              className="mt-4 rounded-md border p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500">
              <Trash2 size={18} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => addMembership("flight", EMPTY_FLIGHT)}
          className="flex items-center gap-2 rounded-lg bg-[#2D0B31]/5 px-4 py-2 text-sm font-bold text-[#2D0B31] transition-colors hover:text-[#4a1352]">
          <Plus size={16} /> Add new flight program
        </button>
      </div>

      <div>
        <h4 className="mb-4 border-b border-gray-100 pb-2 text-lg font-bold text-[#2D0B31]">Hotel Program</h4>
        {hotels.map((item, index) => (
          <div
            key={index}
            className="group relative mb-6 rounded-xl border border-gray-200 bg-gray-50/50 p-5 transition-all hover:border-[#2D0B31]/30">
            <div className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-2">
              <TravelFormSelect
                label="Hotel Chain"
                options={hotelOptions}
                value={item.alliance}
                onChange={(e) => handleMembershipChange("hotel", index, "alliance", e.target.value)}
                required
              />
              <TravelFormInput
                label="Customer Number"
                value={item.memberNumber}
                onChange={(e) => handleMembershipChange("hotel", index, "memberNumber", e.target.value)}
                required
              />
              <TravelFormInput
                label="Customer Request"
                className="md:col-span-2"
                value={item.customerRequest}
                onChange={(e) => handleMembershipChange("hotel", index, "customerRequest", e.target.value)}
              />
            </div>
            <button
              type="button"
              onClick={() => removeMembership("hotel", index)}
              className="mt-4 rounded-md border p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500">
              <Trash2 size={18} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => addMembership("hotel", EMPTY_HOTEL)}
          className="flex items-center gap-2 rounded-lg bg-[#2D0B31]/5 px-4 py-2 text-sm font-bold text-[#2D0B31] transition-colors hover:text-[#4a1352]">
          <Plus size={16} /> Add new hotel program
        </button>
      </div>

      <div>
        <h4 className="mb-4 border-b border-gray-100 pb-2 text-lg font-bold text-[#2D0B31]">Car Program</h4>
        {cars.map((item, index) => (
          <div
            key={index}
            className="group relative mb-6 rounded-xl border border-gray-200 bg-gray-50/50 p-5 transition-all hover:border-[#2D0B31]/30">
            <div className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-2">
              <TravelFormSelect
                label="Company"
                options={carOptions}
                value={item.alliance}
                onChange={(e) => handleMembershipChange("rentalCar", index, "alliance", e.target.value)}
                required
              />
              <TravelFormInput
                label="Customer Number"
                value={item.memberNumber}
                onChange={(e) => handleMembershipChange("rentalCar", index, "memberNumber", e.target.value)}
                required
              />
              <TravelFormInput
                label="Customer Request"
                value={item.customerRequest}
                onChange={(e) => handleMembershipChange("rentalCar", index, "customerRequest", e.target.value)}
              />
              <TravelFormSelect
                label="Special Request"
                options={carSpecialRequestOptions}
                value={item.specialRequest}
                onChange={(e) => handleMembershipChange("rentalCar", index, "specialRequest", e.target.value)}
              />
            </div>
            <button
              type="button"
              onClick={() => removeMembership("rentalCar", index)}
              className="mt-4 rounded-md border p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500">
              <Trash2 size={18} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => addMembership("rentalCar", EMPTY_CAR)}
          className="flex items-center gap-2 rounded-lg bg-[#2D0B31]/5 px-4 py-2 text-sm font-bold text-[#2D0B31] transition-colors hover:text-[#4a1352]">
          <Plus size={16} /> Add new car program
        </button>
      </div>
    </div>
  );
};

export default TravelPreferences;
